
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Form } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { decryptUserID } from '../utils/decryptUserID';

const DeleteAccount = () => {
  const [statusMessage, setStatusMessage] = useState('');
  const [statusVariant, setStatusVariant] = useState('');
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const encryptedUserID = queryParams.get('uid');
  const decryptedUserID = encryptedUserID ? decryptUserID(encryptedUserID) : null;

  useEffect(() => {
    if (!encryptedUserID) {
      setStatusMessage(
        'For a deletion request, please log in to the game and press the "Deletion Request" button in your profile.'
      );
      setStatusVariant('info');
    }
  }, [encryptedUserID]);

  const handleDeleteUserRequest = async () => {
    if (!usernameInput.trim()) {
      setStatusMessage('Please enter your username.');
      setStatusVariant('danger');
      return;
    }

    setDeleteButtonDisabled(true);
    setStatusMessage('');

    try {
      const deleteRequest = {
        username: usernameInput,
        userID: decryptedUserID,
        subject: `Delete Account Request for ${usernameInput}`,
        message: `User ${usernameInput} (UID: ${decryptedUserID}) has requested to delete their account.`
      };

      // Send the email to support - original API endpoint
      const response = await fetch(
        'https://war2escape-6b76ef259e96.herokuapp.com/delete-account',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(deleteRequest)
        }
      );

      // Rate limit exceeded
      if (response.status === 429) {
        setStatusMessage(
          'Too many requests. Please try again later.'
        );
        setStatusVariant('danger');
        setDeleteButtonDisabled(false);
        return;
      }

      if (response.ok) {
        setStatusMessage(
          'Your account deletion request has been sent. We will process it as soon as possible.'
        );
        setStatusVariant('success');

        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        throw new Error('Failed to send the request.');
      }

    } catch (error) {
      console.error('Delete account request error:', error);

      setStatusMessage(
        'There was an error sending your request. Please try again later.'
      );
      setStatusVariant('danger');
      setDeleteButtonDisabled(false);
    }
  };

  return (
    <section id="user-info-actions" className="user-info-actions">
      <Container>
        <Row className="justify-content-center">
          <Col md={8}>
            <h2 className="section-title">Delete Account</h2>

            {statusMessage && (
              <Alert
                variant={statusVariant}
                onClose={() => setStatusMessage('')}
                dismissible
                className="mt-3"
              >
                {statusMessage}
              </Alert>
            )}

            {!encryptedUserID ? (
              <p></p>
            ) : (
              <div className="delete-container">
                <p className="important-text">
                  Are you sure you want to delete your account and all associated data?
                </p>

                <ul className="delete-list">
                  <li>
                    Personal Identifiable Information (PII): Email Address, Username
                  </li>
                  <li>
                    User Profile Data: Player Characteristics, Inventory Items
                  </li>
                  <li>
                    Transaction Data: Purchase History
                  </li>
                  <li>
                    Game Progress and Stats: Achievements and Levels, High Scores
                  </li>
                  <li>
                    Friends and Social Data: Friend Lists, Messages
                  </li>
                </ul>

                <Form.Group controlId="formUsername">
                  <Form.Label>
                    Please enter your username to confirm
                  </Form.Label>

                  <Form.Control
                    type="text"
                    value={usernameInput}
                    onChange={(e) => setUsernameInput(e.target.value)}
                    placeholder="Enter your username"
                    maxLength={100}
                    required
                  />
                </Form.Group>

                <div className="btn-container">
                  <Button
                    onClick={handleDeleteUserRequest}
                    variant="danger"
                    disabled={deleteButtonDisabled}
                  >
                    Yes, Delete My Account
                  </Button>

                  <Button
                    onClick={() => navigate('/')}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default DeleteAccount;
